import PrescriptionSelectionTagList from '@/components/prescriptions/PrescriptionSelectionTagList'
import {
	PrescriptionConfirmationAlert,
	PrescriptionIncompleteAlert,
} from '@/components/prescriptions/PrescriptionStatusAlert'
import PrescriptionWorkSelector from '@/components/prescriptions/PrescriptionWorkSelector'
import {
	CourseSelections,
	courseRequiresForm,
	courseRequiresShakespeare,
	expandSelectorsByQty,
	getCourseConfirmationMessage,
	getCourseStatus,
	getUnmetMessages,
	resolveCourseSelections,
} from '@/components/prescriptions/utils/prescriptionSelectionRules'
import type {
	PrescriptionSelectionCourse,
	PrescriptionSelectionFocusAreaSelector,
} from '@/types/fetchPrescriptionSelection'
import { useId, useMemo } from 'react'

export interface PrescriptionFocusAreaAccordionProps {
	course: PrescriptionSelectionCourse
	courseSelections: CourseSelections | undefined
	onSelectionChange: (_slotIndex: number, _workIds: string[]) => void
	onResetCourse: () => void
}

interface SelectorRow {
	selector: PrescriptionSelectionFocusAreaSelector
	slotIndex: number
}

interface FocusAreaGroup {
	focusArea: string
	rows: SelectorRow[]
}

const buildGroups = (course: PrescriptionSelectionCourse): FocusAreaGroup[] => {
	const expanded = expandSelectorsByQty(course)
	const groups: FocusAreaGroup[] = []
	for (const slot of expanded) {
		const row: SelectorRow = {
			selector: slot.selector,
			slotIndex: slot.flatSlotIndex,
		}
		const last = groups[groups.length - 1]
		if (last && last.focusArea === slot.focusArea) {
			last.rows.push(row)
		} else {
			groups.push({ focusArea: slot.focusArea, rows: [row] })
		}
	}
	return groups
}

export function PrescriptionFocusAreaAccordion({
	course,
	courseSelections,
	onSelectionChange,
	onResetCourse,
}: PrescriptionFocusAreaAccordionProps) {
	const reactId = useId()
	const courseTitle = course['course-title']
	const status = getCourseStatus(course, courseSelections)
	const unmetMessages = useMemo(
		() => getUnmetMessages(course, courseSelections),
		[course, courseSelections],
	)

	const groups = useMemo(() => buildGroups(course), [course])
	const selectedEntries = useMemo(
		() => resolveCourseSelections(course, courseSelections),
		[course, courseSelections],
	)
	const hasShakespeareRule = useMemo(
		() => courseRequiresShakespeare(course),
		[course],
	)
	const hasFormRequirement = useMemo(
		() => courseRequiresForm(course),
		[course],
	)

	return (
		<div className="flex flex-col gap-4">
			<div>
				<table className="w-full border-collapse text-left">
					<thead className="hidden md:table-header-group">
						<tr>
							<th
								scope="col"
								className="font-bold p-3 align-top border-b border-nsw-grey-04 w-2/5"
							>
								Focus area
							</th>
							<th
								scope="col"
								className="font-bold p-3 align-top border-b border-nsw-grey-04"
							>
								Your selection
							</th>
						</tr>
					</thead>
					<tbody>
						{groups.flatMap((group) =>
							group.rows.map((row, rowIdx) => {
								const selectedForRow = selectedEntries.filter(
									(entry) =>
										entry.slotIndex === row.slotIndex,
								)
								const htmlId = `selector-${reactId}-${row.slotIndex}`
								return (
									<tr
										key={row.slotIndex}
										className="flex flex-col md:table-row gap-2 md:gap-0 mb-4 md:mb-0"
									>
										{rowIdx === 0 && (
											<td
												className="hidden md:table-cell p-3 align-top md:w-2/5 md:border-b md:border-nsw-grey-04"
												rowSpan={group.rows.length}
											>
												<span className="font-normal">
													{group.focusArea}
												</span>
											</td>
										)}
										<td className="p-3 align-top md:border-b md:border-nsw-grey-04">
											<span className="md:hidden block font-bold mb-2">
												{group.focusArea}
											</span>
											<PrescriptionWorkSelector
												course={course}
												selector={row.selector}
												slotIndex={row.slotIndex}
												courseSelections={
													courseSelections
												}
												onChange={(workIds) =>
													onSelectionChange(
														row.slotIndex,
														workIds,
													)
												}
												htmlId={htmlId}
												ariaLabel={`Select work for ${group.focusArea}`}
											/>
											{selectedForRow.length > 0 && (
												<div
													className="mt-3 flex flex-wrap"
													data-testid="selection-tag-list"
												>
													{selectedForRow.map(
														(entry, idx) => (
															<PrescriptionSelectionTagList
																key={`${entry.work.id}-${idx}`}
																work={
																	entry.work
																}
																courseTitle={
																	courseTitle
																}
																hasShakespeareRule={
																	hasShakespeareRule
																}
																hasFormRequirement={
																	hasFormRequirement
																}
															/>
														),
													)}
												</div>
											)}
										</td>
									</tr>
								)
							}),
						)}
					</tbody>
				</table>
			</div>

			{status !== 'complete' && (
				<PrescriptionIncompleteAlert messages={unmetMessages} />
			)}

			{status === 'complete' && (
				<PrescriptionConfirmationAlert
					message={getCourseConfirmationMessage(course)}
					courseTitle={courseTitle}
				/>
			)}

			<div className="flex justify-end">
				<button
					type="button"
					className="underline font-bold nsw-text--brand-dark"
					onClick={onResetCourse}
					data-testid="prescription-reset-course"
				>
					Reset selections
				</button>
			</div>
		</div>
	)
}

export default PrescriptionFocusAreaAccordion
